"use client"

import { useState, useEffect, useCallback } from "react"
import { collection, getDocs, doc, getDoc } from "firebase/firestore"
import { db } from "../../firebaseconfig"
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts"
import { formatCurrency } from "../../utils/formatCurrency"

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#AF19FF", "#666666"]

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  const RADIAN = Math.PI / 180
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      className="graph-text"
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

const AreaBudgetVisualization = ({ selectedArea }) => {
  const [areaData, setAreaData] = useState(null)
  const [expandedCharts, setExpandedCharts] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const fetchBudgetData = useCallback(async () => {
    if (!selectedArea) return

    try {
      const budgetDocRef = doc(db, "budgets", selectedArea)
      const budgetDocSnap = await getDoc(budgetDocRef)

      if (!budgetDocSnap.exists()) {
        throw new Error("Area not found in budgets collection")
      }

      const areaData = { id: selectedArea, ...budgetDocSnap.data() }

      const subAreasQuery = await getDocs(collection(db, "budgets"))
      const subAreas = subAreasQuery.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((budget) => budget.type === "subarea" && budget.from === selectedArea)

      // Fetch obras for the main area
      const areaCollection = collection(db, selectedArea)
      const areaSnapshot = await getDocs(areaCollection)
      const areaObras = areaSnapshot.docs
        .map((doc) => {
          const { obra, costo, metadata, ...rest } = doc.data()
          return {
            name: obra,
            value: Number.parseFloat(costo) || 0,
            ...rest,
          }
        })
        .filter((obra) => obra.name !== "metadata")

      const subAreasData = await Promise.all(
        subAreas.map(async (subArea) => {
          const subAreaCollection = collection(db, subArea.id)
          const subAreaSnapshot = await getDocs(subAreaCollection)
          const obras = subAreaSnapshot.docs
            .map((doc) => {
              const { obra, costo, metadata, ...rest } = doc.data()
              return {
                name: obra,
                value: Number.parseFloat(costo) || 0,
                ...rest,
              }
            })
            .filter((obra) => obra.name !== "metadata")

          return {
            name: subArea.id,
            amount: Number.parseFloat(subArea.amount) || 0,
            obras,
            usedBudget: Number.parseFloat(subArea.used) || 0,
            remainingBudget: Math.max(
              (Number.parseFloat(subArea.amount) || 0) - (Number.parseFloat(subArea.used) || 0),
              0,
            ),
          }
        }),
      )

      setAreaData({
        name: areaData.id,
        amount: Number.parseFloat(areaData.amount) || 0,
        subAreas: subAreasData,
        usedBudget: Number.parseFloat(areaData.used) || 0,
        areaUsedBudget: areaObras.reduce((sum, obra) => sum + obra.value, 0),
        areaObras: areaObras,
      })
    } catch (error) {
      console.error("Error fetching budget data:", error)
    }
  }, [selectedArea])

  useEffect(() => {
    if (selectedArea) {
      fetchBudgetData()
    }
  }, [selectedArea, fetchBudgetData]) // Removed refreshKey from dependencies

  const handleReload = useCallback(() => {
    setRefreshKey((prevKey) => prevKey + 1)
  }, [])

  if (!selectedArea || !areaData) {
    return <div>Loading...</div>
  }

  const subAreaChartData = [
    { name: `${areaData.name} (Secretaría)`, value: areaData.areaUsedBudget },
    ...areaData.subAreas.map((subArea) => ({
      name: `${subArea.name} (Área)`,
      value: subArea.usedBudget,
    })),
  ]

  const remaining = Math.max(areaData.amount - areaData.usedBudget, 0)
  if (remaining > 0) {
    subAreaChartData.push({ name: "Presupuesto Restante", value: remaining })
  }

  const toggleExpandCharts = () => {
    setExpandedCharts(!expandedCharts)
  }

  return (
    <div className="budget-chart">
      <h2>Presupuesto {areaData.name}</h2>
      <div className="chart-content">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={subAreaChartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
            >
              {subAreaChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const item = payload[0].payload
                  const percentage = (item.value / areaData.amount) * 100
                  return (
                    <div className="area-hover">
                      <p>{item.name}</p>
                      <p>
                        {formatCurrency(item.value)} ({percentage.toFixed(2)}%)
                      </p>
                    </div>
                  )
                }
                return null
              }}
            />
            <Legend
              formatter={(value, entry) => {
                const item = subAreaChartData.find((d) => d.name === value)
                const percentage = (item.value / areaData.amount) * 100
                return `${value}: ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`
              }}
              wrapperStyle={{ fontSize: "12px" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="budget-summary">
        <p className="total-budget">Presupuesto Total: {formatCurrency(areaData.amount)}</p>
        <p className="used-budget">Presupuesto Utilizado: {formatCurrency(areaData.usedBudget)}</p>
        {areaData.usedBudget <= areaData.amount ? (
          <p className="remaining-budget">
            Presupuesto Restante: {formatCurrency(areaData.amount - areaData.usedBudget)}
          </p>
        ) : (
          <p className="exceeded-budget">
            Presupuesto Excedido: {formatCurrency(areaData.usedBudget - areaData.amount)} (
            {((areaData.usedBudget / areaData.amount - 1) * 100).toFixed(2)}%)
          </p>
        )}
      </div>
      <div className="btn-container-area">
        <button onClick={handleReload} className="btn btn-reload">
          Recargar
        </button>
        <button onClick={toggleExpandCharts} className="btn btn-expand">
          {expandedCharts ? "Contraer" : "Expandir"}
        </button>
      </div>
      {expandedCharts && (
        <div className="expanded-charts">
          <div className="area-obras-chart">
            <h4>{areaData.name.toUpperCase()}</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    ...areaData.areaObras,
                    {
                      name: "Restante",
                      value: Math.max(
                        areaData.amount -
                          areaData.areaUsedBudget -
                          areaData.subAreas.reduce((sum, subArea) => sum + subArea.amount, 0),
                        0,
                      ),
                    },
                  ].filter((item) => item.name && item.value !== undefined)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    ...areaData.areaObras,
                    {
                      name: "Restante",
                      value: Math.max(
                        areaData.amount -
                          areaData.areaUsedBudget -
                          areaData.subAreas.reduce((sum, subArea) => sum + subArea.amount, 0),
                        0,
                      ),
                    },
                  ]
                    .filter((item) => item.name && item.value !== undefined)
                    .map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const item = payload[0].payload
                      const totalDirectBudget =
                        areaData.amount - areaData.subAreas.reduce((sum, subArea) => sum + subArea.amount, 0)
                      const percentage = (item.value / totalDirectBudget) * 100
                      return (
                        <div className="area-hover">
                          <p>{item.name}</p>
                          <p>
                            {formatCurrency(item.value)} ({percentage.toFixed(2)}%)
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Legend
                  formatter={(value, entry) => {
                    const item = [
                      ...areaData.areaObras,
                      {
                        name: "Restante",
                        value: Math.max(
                          areaData.amount -
                            areaData.areaUsedBudget -
                            areaData.subAreas.reduce((sum, subArea) => sum + subArea.amount, 0),
                          0,
                        ),
                      },
                    ]
                      .filter((d) => d.name && d.value !== undefined)
                      .find((d) => d.name === value)
                    if (item) {
                      const totalDirectBudget =
                        areaData.amount - areaData.subAreas.reduce((sum, subArea) => sum + subArea.amount, 0)
                      const percentage = (item.value / totalDirectBudget) * 100
                      return `${value}: ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`
                    }
                    return value
                  }}
                  wrapperStyle={{ fontSize: "12px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {areaData.subAreas.map((subArea) => (
            <div key={subArea.name} className="sub-area-chart">
              <h4>{subArea.name}</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[...subArea.obras, { name: "Restante", value: subArea.remainingBudget }].filter(
                      (item) => item.name && item.value !== undefined,
                    )}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[...subArea.obras, { name: "Restante", value: subArea.remainingBudget }]
                      .filter((item) => item.name && item.value !== undefined)
                      .map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0].payload
                        const percentage = (item.value / subArea.amount) * 100
                        return (
                          <div className="area-hover">
                            <p>{item.name}</p>
                            <p>
                              {formatCurrency(item.value)} ({percentage.toFixed(2)}%)
                            </p>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend
                    formatter={(value, entry) => {
                      const item = [...subArea.obras, { name: "Restante", value: subArea.remainingBudget }]
                        .filter((d) => d.name && d.value !== undefined)
                        .find((d) => d.name === value)
                      if (item) {
                        const percentage = (item.value / subArea.amount) * 100
                        return `${value}: ${formatCurrency(item.value)} (${percentage.toFixed(2)}%)`
                      }
                      return value
                    }}
                    wrapperStyle={{ fontSize: "12px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AreaBudgetVisualization

